import { useEffect, useRef, useState } from "react";
import { getBookingStatusLabel } from "../utils/bookingStatusDisplay";

function schedule(date, time) {
  const datePart = String(date || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart) || !/^\d{2}:\d{2}$/.test(time || "")) {
    return { date: datePart || "Unavailable", time: time || "Time unavailable", weekday: "", value: null };
  }
  const [y, m, d] = datePart.split("-").map(Number);
  const [h, min] = time.split(":").map(Number);
  // Preserve the stored wall-clock schedule regardless of the admin device timezone.
  // This timestamp is for display/duration only; the backend owns rental calculations.
  const value = new Date(Date.UTC(y, m - 1, d, h, min));
  if (value.getUTCFullYear() !== y || value.getUTCMonth() !== m - 1 || value.getUTCDate() !== d ||
      value.getUTCHours() !== h || value.getUTCMinutes() !== min) {
    return { date: "Unavailable", time: "Time unavailable", weekday: "", value: null };
  }
  return {
    date: value.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }),
    time: value.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "UTC" }),
    weekday: value.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" }),
    value,
  };
}

function durationLabel(current, requested) {
  if (!current.value || !requested.value) return "";
  const minutes = Math.round((requested.value - current.value) / 60000);
  if (minutes <= 0) return "";
  const days = Math.floor(minutes / 1440), hours = Math.floor((minutes % 1440) / 60), rest = minutes % 60;
  return "+" + [days ? days + (days === 1 ? " day" : " days") : "",
    hours ? hours + (hours === 1 ? " hour" : " hours") : "", rest ? rest + " min" : ""].filter(Boolean).join(" ");
}

function money(value) {
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
    ? "PHP " + Number(value).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;
}

function CalendarIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <rect x="4" y="5" width="16" height="16" rx="3" /><path d="M8 3v4m8-4v4M4 10h16" />
  </svg>;
}

export default function ExtensionReviewModal({ booking, request, onClose, onDecision }) {
  const [notes, setNotes] = useState("");
  const [decision, setDecision] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [updatedPricing, setUpdatedPricing] = useState(null);
  const pricing = updatedPricing || request.pricing;
  const submittingRef = useRef(false);
  const reviewRef = useRef(null);
  const confirmationRef = useRef(null);
  const current = schedule(booking.endDate, booking.endTime);
  const requested = schedule(request.requestedEndDate, request.requestedEndTime);
  const duration = durationLabel(current, requested);
  const customer = booking.fullName || booking.customerName || "Customer";
  const reference = booking.bookingReference || booking.reference || booking._id;
  const vehicle = booking.vehicleId || {};
  const vehicleLabel = [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(" ") || booking.vehicleName || "Vehicle unavailable";
  const charge = money(pricing?.extensionCharge ?? request.additionalAmount);
  const pending = String(request.status || "").toLowerCase() === "pending";
  const metrics = [
    ["Current Booking Total", money(pricing?.currentTotal ?? request.oldTotal)],
    ["+ Extension Charge", charge],
    ["New Booking Total", money(pricing?.newTotal ?? request.newTotal)],
  ].filter(([, value]) => value !== null);
  const paymentMetrics = pricing ? [
    ["Already Paid", money(pricing.amountAlreadyPaid)],
    ["Existing Unpaid Balance", money(pricing.existingUnpaidBalance)],
    ["Amount to Collect", money(pricing.amountToCollect)],
  ].filter(([, value]) => value !== null) : [];
  const summary = [
    ["Customer", customer], ["Booking reference", reference],
    ...(decision === "approve" ? [["Current return", current.date + " \u00B7 " + current.time]] : []),
    [decision === "approve" ? "New return" : "Requested return", requested.date + " \u00B7 " + requested.time],
    ...(duration ? [["Extension duration", duration]] : []),
    ...(decision === "approve" ? [...metrics, ...paymentMetrics] : []),
  ];

  useEffect(() => {
    const previousFocus = document.activeElement;
    const panel = decision ? confirmationRef.current : reviewRef.current;
    panel?.querySelector("button:not(:disabled)")?.focus();
    const keydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault(); event.stopPropagation();
        if (!submittingRef.current) { if (decision) setDecision(null); else onClose(); }
      }
      if (event.key !== "Tab") return;
      const items = [...(panel?.querySelectorAll("button:not(:disabled), textarea:not(:disabled)") || [])];
      const first = items[0], last = items[items.length - 1];
      if (!first) { event.preventDefault(); panel?.focus(); }
      else if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keydown, true);
    return () => {
      document.removeEventListener("keydown", keydown, true);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [decision, onClose]);

  const close = () => { if (!submittingRef.current) onClose(); };
  const confirm = async () => {
    if (submittingRef.current || !decision || !pending) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError("");
    try {
      const result = await onDecision(decision, notes, decision === "approve" ? updatedPricing?.pricingKey : undefined);
      if (result?.ok) { setDecision(null); onClose(); }
      else if (result?.pricing) { setUpdatedPricing(result.pricing); setError(""); }
      else setError(result?.message || "Unable to review this extension. Please try again.");
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to review this extension. Please try again.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  const choose = (action) => { setError(""); setDecision(action); };

  return <div className="modal-overlay extension-review-overlay" onClick={() => { if (!decision) close(); }}>
    <section ref={reviewRef} className="extension-review-modal" role="dialog" aria-modal="true" aria-labelledby="extension-review-title" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
      <div className="extension-review-content" inert={decision ? true : undefined}>
        <header className="extension-review-header">
          <div><span className="manual-booking-eyebrow">Rental extension</span>
            <h2 id="extension-review-title">Review Extension Request</h2>
            <p>Review the requested return schedule and decide whether to approve the extension.</p></div>
          <button className="extension-review-close" type="button" onClick={close} disabled={submitting} aria-label="Close extension review">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </header>
        <div className="extension-review-body">
          <dl className="extension-review-identity">
            <div><dt>Customer</dt><dd>{customer}</dd>{booking.contact ? <small>{booking.contact}</small> : null}</div>
            <div><dt>Booking reference</dt><dd>{reference}</dd></div>
            <div><dt>Vehicle</dt><dd>{vehicleLabel}</dd>{vehicle.plateNumber ? <small>Plate No. {vehicle.plateNumber}</small> : null}</div>
            <div><dt>Current status</dt><dd><span className={"extension-review-status" + (["confirmed", "booked_confirmed", "active", "ongoing"].includes(booking.status) ? " is-confirmed" : "")}>{getBookingStatusLabel(booking.status)}</span></dd></div>
            <div><dt>Payment status</dt><dd><span className={"extension-review-status" + (booking.paymentStatus === "verified" ? " is-confirmed" : "")}>{String(booking.paymentStatus || "Unavailable").replace(/_/g, " ")}</span></dd></div>
          </dl>
          <div className="extension-review-schedules">
            <div className="extension-review-schedule"><CalendarIcon /><div><span>Current return</span><strong>{current.date}</strong><b>{current.time}</b><small>{current.weekday}</small></div></div>
            <div className="extension-review-duration"><span aria-hidden="true">{"\u2192"}</span>{duration ? <b>{duration}</b> : <small>Requested schedule</small>}</div>
            <div className="extension-review-schedule extension-review-schedule--requested"><CalendarIcon /><div><span>Requested extension</span><strong>{requested.date}</strong><b>{requested.time}</b><small>{requested.weekday}</small></div></div>
          </div>
          <section><h3>Customer reason</h3><blockquote className="extension-review-reason">{request.reason || "No reason provided."}</blockquote></section>
          <section><h3>Pricing &amp; payment impact</h3>
            {metrics.length ? <div className="extension-review-metrics">{metrics.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div> : <p>Pricing is unavailable for this request.</p>}
            {pricing?.extensionDuration?.rateLabel ? <p className="extension-review-rate-label">{pricing.extensionDuration.rateLabel}</p> : null}
            {paymentMetrics.length ? <><h3>Payment to collect</h3><div className="extension-review-metrics extension-review-payment-metrics">{paymentMetrics.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div></> : null}
            <p className="extension-review-pricing-note">Request pricing shown. Original discounts remain on the existing booking; the new interval uses current standard rates. Any existing unpaid balance is collected separately from the extension charge. Pricing and availability are revalidated on approval.</p>
          </section>
          <div className="extension-review-notice"><span aria-hidden="true">i</span><div>
            <p>Approving this extension will update the booking return schedule to <strong>{requested.date}, {requested.time}</strong>.</p>
            <p>{charge && Number(pricing?.extensionCharge ?? request.additionalAmount) > 0
              ? "The request includes an additional extension charge of " + charge + ". Approval creates an additional-payment request; payment is collected separately."
              : "Any additional charge confirmed on approval will be requested separately. Approval does not process payment automatically."}</p>
          </div></div>
          <label className="extension-review-notes">Admin notes (optional)
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={submitting} placeholder="Add notes or remarks about this extension request..." rows={3} />
          </label>
          {!pending ? <p role="status">This request is no longer pending. Close this review to see its latest status.</p> : null}
        </div>
        <footer className="extension-review-footer">
          <button type="button" className="btn btn-soft" onClick={close} disabled={submitting}>Close</button>
          <button type="button" className="btn btn-danger-outline" onClick={() => choose("reject")} disabled={submitting || !pending}>Decline Request</button>
          <button type="button" className="btn btn-primary" onClick={() => choose("approve")} disabled={submitting || !pending}>Approve Extension</button>
        </footer>
      </div>
      {decision ? <div className="extension-review-confirm-overlay">
        <section ref={confirmationRef} className="bdc-confirm-box extension-review-confirm" role="dialog" aria-modal="true" aria-labelledby="extension-decision-title" aria-busy={submitting} tabIndex={-1}>
          <h3 id="extension-decision-title">{decision === "approve" ? "Approve extension request?" : "Decline extension request?"}</h3>
          <p>{decision === "approve" ? "Confirm that you have reviewed the extension request and its booking/payment impact." : "Are you sure you want to decline this extension request?"}</p>
          {updatedPricing && decision === "approve" ? <div className="extension-review-notice" role="alert"><div><strong>Pricing updated</strong><p>Original extension quote: {money(request.additionalAmount)}</p><p>Updated extension charge: {money(updatedPricing.extensionCharge)}</p><p>Current vehicle pricing and booking balances were revalidated. Review the updated totals before approving.</p></div></div> : null}
          <dl className="bdc-confirm-summary">{summary.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
          {decision === "reject" ? <label className="extension-review-notes">Decline reason / remarks (optional)<textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={submitting} rows={3} /></label> : null}
          {error ? <p className="bdc-confirm-error" role="alert">{error}</p> : null}
          <div className="bdc-confirm-box__buttons">
            <button type="button" className="btn btn-soft" disabled={submitting} onClick={() => { if (!submittingRef.current) setDecision(null); }}>Cancel</button>
            <button type="button" className={decision === "approve" ? "btn btn-primary" : "btn btn-danger"} disabled={submitting || !pending} onClick={confirm}>
              {submitting ? (decision === "approve" ? "Approving..." : "Declining...") : (decision === "approve" ? updatedPricing ? "Approve Updated Amount" : "Confirm Approval" : "Confirm Decline")}
            </button>
          </div>
        </section>
      </div> : null}
    </section>
  </div>;
}
